/**
 * jai1 tasks add <title> [-p priority] [-P parent] [-t tags] [--prompt] [-j]
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { TaskService } from '../../services/task.service.js';
import { PRIORITY_ICONS, PRIORITY_LABELS } from '../../types/task.types.js';
import type { TaskAddOptions } from '../../types/task.types.js';

export function createTaskAddCommand(): Command {
    return new Command('add')
        .description('Add a new task')
        .argument('<title>', 'Task title')
        .option('-p, --priority <n>', 'Priority: 0=critical, 1=high, 2=medium, 3=low', '2')
        .option('-P, --parent <parent>', 'Parent: feature/xxx, bug/xxx, plan/xxx, task/xxx, prd/xxx')
        .option('-t, --tags <tags>', 'Comma-separated tags')
        .option('--prompt', 'Type: prompt — free-form instruction, no doc reference required')
        .option('-j, --json', 'Output JSON')
        .action(async (title: string, options: TaskAddOptions) => {
            const service = new TaskService();
            const priority = Number(options.priority ?? 2);

            if (priority < 0 || priority > 3) {
                console.error(chalk.red('❌ Priority must be 0-3'));
                process.exit(1);
            }

            const tags = options.tags
                ? options.tags.split(',').map((t: string) => t.trim())
                : [];

            const taskType = options.prompt ? 'prompt' : 'task';

            const task = await service.add({
                title,
                type: taskType,
                parent: options.parent || '',
                priority,
                tags,
            });

            if (options.json) {
                console.log(JSON.stringify(task, null, 2));
                return;
            }

            const icon = PRIORITY_ICONS[task.priority] || '🟡';
            const label = PRIORITY_LABELS[task.priority] || 'Medium';
            const typeLabel = task.type === 'prompt' ? chalk.magenta(' [prompt]') : '';

            console.log(chalk.green(`✅ Task added: ${chalk.bold(task.id)}${typeLabel}`));
            console.log(`   ${chalk.dim('Title:')}    ${task.title}`);
            console.log(`   ${chalk.dim('Priority:')} ${icon} ${label}`);
            if (task.parent) {
                console.log(`   ${chalk.dim('Parent:')}   ${task.parent}`);
            }
            if (task.tags.length > 0) {
                console.log(`   ${chalk.dim('Tags:')}     ${task.tags.join(', ')}`);
            }
        });
}
